/** Screen-vs-consolidation tie-out (QOE-RECON-1). Gary's stated minimum bar
 *  for QoE: "line items align on the screen vs what the consolidation
 *  produces." Four blocks per period, built to the structure and copy of
 *  _ClaudeCode_Briefs/QOE-RECON-1_approved_screen_mock.html:
 *    1. Contribution by entity (as actually taken into the consolidation)
 *    2. Intercompany eliminations, itemised with amounts
 *    3. Excluded — no treatment selected (flexible line items nobody routed)
 *    4. Consolidated, with the tie printed beneath a double-rule total
 *
 *  no-mint throughout: an absent figure renders as an em dash, never `$0` —
 *  see shared.ts `fmtMoneyOrDash`. This file only reads `ConsolidationResult`
 *  and the deal's flexible items; it computes nothing that feeds back into
 *  the consolidation itself. */
import { useEffect, useState } from 'react'
import { CheckCircle, XCircle } from 'lucide-react'
import type {
  ConsolidationResult,
  ConsolidatedPeriod,
  DealEntity,
  EliminationItem,
  ConsolidationTie,
} from '@/api/client'
import { listFlexibleItems } from '@/api/client'
import { routeFlexibleItems } from '@/lib/flexibleLineItems'
import type { FlexibleLineItem, RoutedSummary } from '@/lib/flexibleLineItems'
import { fmtMoneyOrDash } from './shared'

export default function ReconciliationPanel({
  dealId,
  result,
}: {
  dealId: string
  result: ConsolidationResult
  entities: DealEntity[]
}) {
  const [flexItems, setFlexItems] = useState<FlexibleLineItem[]>([])
  const [flexLoaded, setFlexLoaded] = useState(false)

  useEffect(() => {
    let cancelled = false
    setFlexLoaded(false)
    listFlexibleItems(dealId)
      .then(data => {
        if (!cancelled) setFlexItems(data.items)
      })
      .catch(() => {
        // Block 3 degrades to "nothing to show" rather than blocking the rest
        // of the tie-out on a secondary fetch.
      })
      .finally(() => {
        if (!cancelled) setFlexLoaded(true)
      })
    return () => {
      cancelled = true
    }
  }, [dealId])

  const eliminations = (result.qoe_finding?.eliminations as EliminationItem[] | undefined) ?? []

  return (
    <div className="space-y-6">
      {result.consolidated_periods.map(period => (
        <PeriodReconciliation
          key={period.period_label}
          period={period}
          eliminations={eliminations.filter(e => e.period === period.period_label)}
          flexItems={flexItems}
          flexLoaded={flexLoaded}
        />
      ))}
    </div>
  )
}

function PeriodReconciliation({
  period,
  eliminations,
  flexItems,
  flexLoaded,
}: {
  period: ConsolidatedPeriod
  eliminations: EliminationItem[]
  flexItems: FlexibleLineItem[]
  flexLoaded: boolean
}) {
  const tie = period.tie
  const contributions = period.entity_contributions ?? []
  const retained = period.retained_entities ?? []

  const ownerCompRows = contributions
    .map(ec => ec.owner_comp_total)
    .filter((v): v is number => v !== null && v !== undefined)
  const subtotalOwnerComp = ownerCompRows.length
    ? ownerCompRows.reduce((a, b) => a + b, 0)
    : null

  return (
    <div className="border rounded-lg p-4 space-y-6">
      <h3 className="font-medium text-gray-800">
        {period.statement_label ?? 'Consolidated'} — {period.period_label}
      </h3>

      {/* Block 1: Contribution by entity */}
      <div>
        <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
          Contribution by Entity
        </h4>
        <div className="overflow-x-auto">
          <table className="w-full text-sm tabular-nums">
            <thead className="border-b">
              <tr>
                <th className="text-left px-3 py-1.5 font-medium text-gray-600">Entity</th>
                <th className="text-left px-3 py-1.5 font-medium text-gray-600">Method</th>
                <th className="text-right px-3 py-1.5 font-medium text-gray-600">Revenue</th>
                <th className="text-right px-3 py-1.5 font-medium text-gray-600">EBITDA</th>
                <th className="text-right px-3 py-1.5 font-medium text-gray-600">Owner Comp</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {contributions.map(ec => (
                <tr key={ec.entity_id}>
                  <td className="px-3 py-1.5 text-gray-900">{ec.entity_name}</td>
                  <td className="px-3 py-1.5 text-gray-600 text-xs">
                    {ec.consolidation_method === 'equity' ? (
                      <>
                        <span className="bg-indigo-100 text-indigo-700 px-1.5 py-0.5 rounded text-xs">
                          Income only
                        </span>
                        {ec.note && (
                          <span className="block mt-0.5 text-gray-500">{ec.note}</span>
                        )}
                      </>
                    ) : (
                      ec.note ?? '—'
                    )}
                  </td>
                  <td className="px-3 py-1.5 text-right">{fmtMoneyOrDash(ec.revenue)}</td>
                  <td className="px-3 py-1.5 text-right">{fmtMoneyOrDash(ec.ebitda)}</td>
                  <td className="px-3 py-1.5 text-right">{fmtMoneyOrDash(ec.owner_comp_total)}</td>
                </tr>
              ))}
              {retained.map(name => (
                <tr key={`retained-${name}`} className="bg-amber-50">
                  <td className="px-3 py-1.5 text-gray-900">
                    {name}
                    <span className="ml-2 text-xs bg-amber-100 text-amber-800 px-1.5 py-0.5 rounded">
                      Carved out
                    </span>
                  </td>
                  <td className="px-3 py-1.5 text-amber-700 text-xs">
                    Excluded — ASC 850 disclosure
                  </td>
                  <td className="px-3 py-1.5 text-right text-amber-700 text-xs">excluded</td>
                  <td className="px-3 py-1.5 text-right text-amber-700 text-xs">excluded</td>
                  <td className="px-3 py-1.5 text-right">{'—'}</td>
                </tr>
              ))}
              <tr className="border-t-2 border-gray-900 font-semibold">
                <td className="px-3 py-1.5" colSpan={2}>Subtotal before eliminations</td>
                <td className="px-3 py-1.5 text-right">{fmtMoneyOrDash(tie?.subtotal_revenue)}</td>
                <td className="px-3 py-1.5 text-right">{fmtMoneyOrDash(tie?.subtotal_ebitda)}</td>
                <td className="px-3 py-1.5 text-right">{fmtMoneyOrDash(subtotalOwnerComp)}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      {/* Block 2: Intercompany eliminations */}
      <div>
        <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
          Intercompany Eliminations
        </h4>
        {eliminations.length === 0 ? (
          <p className="text-sm text-gray-400">No intercompany eliminations this period.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm tabular-nums">
              <thead className="border-b">
                <tr>
                  <th className="text-left px-3 py-1.5 font-medium text-gray-600">Between</th>
                  <th className="text-left px-3 py-1.5 font-medium text-gray-600">Item</th>
                  <th className="text-left px-3 py-1.5 font-medium text-gray-600">Kind</th>
                  <th className="text-right px-3 py-1.5 font-medium text-gray-600">Revenue</th>
                  <th className="text-right px-3 py-1.5 font-medium text-gray-600">EBITDA</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {eliminations.map((e, i) => (
                  <EliminationRow key={`${e.step}-${i}`} e={e} />
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Block 3: Excluded — no treatment selected */}
      <ExcludedBlock period={period} flexItems={flexItems} flexLoaded={flexLoaded} />

      {/* Block 4: Consolidated, with the tie */}
      <div>
        <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
          Consolidated
        </h4>
        <div className="overflow-x-auto">
          <table className="w-full text-sm tabular-nums">
            <tbody>
              <tr className="border-t-2 border-gray-900 border-b-4 border-double font-semibold">
                <td className="px-3 py-1.5">{period.statement_label ?? 'Consolidated'}</td>
                <td className="px-3 py-1.5 text-right">{fmtMoneyOrDash(period.revenue)}</td>
                <td className="px-3 py-1.5 text-right">{fmtMoneyOrDash(period.ebitda)}</td>
              </tr>
            </tbody>
          </table>
        </div>
        <TieBar tie={tie} />
      </div>
    </div>
  )
}

/** Kind-specific copy + sign for one elimination row. Counts are not
 *  evidence; amounts are — every row shows amount_a/amount_b, not just a
 *  step count (QOE-RECON-1 Order step 5). */
function EliminationRow({ e }: { e: EliminationItem }) {
  const meta = eliminationKindMeta(e)
  return (
    <tr className={meta.rowClass}>
      <td className="px-3 py-1.5 text-gray-700 text-xs">
        {e.entity_a}
        {e.entity_b ? ` ↔ ${e.entity_b}` : ''}
      </td>
      <td className="px-3 py-1.5 text-gray-900">
        {e.description}
        <span className="block text-xs text-gray-500">
          {fmtMoneyOrDash(e.amount_a)}
          {e.amount_b !== null && e.amount_b !== undefined && e.amount_b !== e.amount_a
            ? ` / ${fmtMoneyOrDash(e.amount_b)}`
            : ''}
        </span>
      </td>
      <td className="px-3 py-1.5">
        <span className={`text-xs px-1.5 py-0.5 rounded ${meta.chipClass}`}>{meta.label}</span>
      </td>
      <td className="px-3 py-1.5 text-right">{meta.revenueCell}</td>
      <td className="px-3 py-1.5 text-right">{meta.ebitdaCell}</td>
    </tr>
  )
}

function eliminationKindMeta(e: EliminationItem) {
  const revenueAmt = fmtMoneyOrDash(-Math.abs(e.amount_a))
  const ebitdaAmt = fmtMoneyOrDash(e.ebitda_impact)

  if (!e.auto_eliminate) {
    return {
      label: 'Held for review',
      chipClass: 'bg-yellow-100 text-yellow-800',
      rowClass: 'bg-yellow-50',
      revenueCell: <span className="text-yellow-700 text-xs">not netted</span>,
      ebitdaCell: <span className="text-yellow-700 text-xs">not netted</span>,
    }
  }

  switch (e.elimination_kind) {
    case 'revenue_expense':
      return {
        label: 'Revenue / expense',
        chipClass: 'bg-gray-100 text-gray-700',
        rowClass: '',
        revenueCell: revenueAmt,
        ebitdaCell: <span className="text-gray-500 text-xs">— neutral</span>,
      }
    case 'interest':
      return {
        label: 'Intercompany interest',
        chipClass: 'bg-blue-100 text-blue-700',
        rowClass: '',
        revenueCell: <span className="text-gray-400">{'—'}</span>,
        ebitdaCell: ebitdaAmt,
      }
    case 'balance_sheet':
      return {
        label: 'Balance sheet',
        chipClass: 'bg-gray-100 text-gray-700',
        rowClass: '',
        revenueCell: <span className="text-gray-500 text-xs">P&amp;L-neutral</span>,
        ebitdaCell: <span className="text-gray-500 text-xs">P&amp;L-neutral</span>,
      }
    case 'unrealized_inventory_profit':
      return {
        label: 'Unrealized inventory profit',
        chipClass: 'bg-purple-100 text-purple-700',
        rowClass: '',
        revenueCell: <span className="text-gray-400">{'—'}</span>,
        ebitdaCell: ebitdaAmt,
      }
    case 'related_party':
      return {
        label: 'Related party (ASC 850)',
        chipClass: 'bg-amber-100 text-amber-800',
        rowClass: 'bg-amber-50',
        revenueCell: <span className="text-amber-700 text-xs">not eliminated</span>,
        ebitdaCell: <span className="text-amber-700 text-xs">not eliminated</span>,
      }
    default:
      return {
        label: e.elimination_kind,
        chipClass: 'bg-gray-100 text-gray-700',
        rowClass: '',
        revenueCell: revenueAmt,
        ebitdaCell: ebitdaAmt,
      }
  }
}

function reasonLabel(reason: RoutedSummary['reason']): string {
  switch (reason) {
    case 'missing_year':
      return 'No fiscal year assigned'
    case 'missing_treatment':
    case 'unknown_treatment':
    default:
      return 'No treatment selected'
  }
}

function ExcludedBlock({
  period,
  flexItems,
  flexLoaded,
}: {
  period: ConsolidatedPeriod
  flexItems: FlexibleLineItem[]
  flexLoaded: boolean
}) {
  const routed = routeFlexibleItems(flexItems, period.fiscal_year)
  const excludedItems = routed.unrouted
  const subtotal = excludedItems.reduce((sum, item) => sum + item.amount, 0)
  const ebitdaBase = period.ebitda
  const pctOfEbitda =
    ebitdaBase && Math.abs(ebitdaBase) > 0
      ? Math.abs((subtotal / ebitdaBase) * 100)
      : null

  return (
    <div>
      <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
        Excluded — No Treatment Selected
      </h4>
      {!flexLoaded ? (
        <p className="text-sm text-gray-400">Loading…</p>
      ) : excludedItems.length === 0 ? (
        <p className="text-sm text-gray-400">Every flexible line item has a treatment.</p>
      ) : (
        <>
          <div className="overflow-x-auto">
            <table className="w-full text-sm tabular-nums">
              <thead className="border-b">
                <tr>
                  <th className="text-left px-3 py-1.5 font-medium text-gray-600">Line Item</th>
                  <th className="text-left px-3 py-1.5 font-medium text-gray-600">Source</th>
                  <th className="text-left px-3 py-1.5 font-medium text-gray-600">Reason</th>
                  <th className="text-right px-3 py-1.5 font-medium text-gray-600">Amount</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {excludedItems.map((item, i) => (
                  <tr key={`${item.label}-${i}`}>
                    <td className="px-3 py-1.5 text-gray-900">{item.label}</td>
                    <td className="px-3 py-1.5 text-gray-600 text-xs">{item.source}</td>
                    <td className="px-3 py-1.5 text-gray-600 text-xs">{reasonLabel(item.reason)}</td>
                    <td className="px-3 py-1.5 text-right">{fmtMoneyOrDash(item.amount)}</td>
                  </tr>
                ))}
                <tr className="border-t-2 border-gray-900 font-semibold">
                  <td className="px-3 py-1.5" colSpan={3}>Excluded, pending treatment</td>
                  <td className="px-3 py-1.5 text-right">{fmtMoneyOrDash(subtotal)}</td>
                </tr>
              </tbody>
            </table>
          </div>
          <p className="mt-2 text-xs text-gray-500">
            Adjusted EBITDA could move by up to {fmtMoneyOrDash(subtotal)}
            {pctOfEbitda !== null ? ` — ${pctOfEbitda.toFixed(0)}% of consolidated EBITDA` : ''} —
            depending how these {excludedItems.length} item{excludedItems.length === 1 ? '' : 's'} are treated.
          </p>
        </>
      )}
    </div>
  )
}

/** THE TIE MUST BE VISIBLE AND MUST FOOT. Failure is rendered loudly — role
 *  "alert", thick red border — rather than as a small warning, per brief:
 *  "the screen says so loudly rather than rendering a total that silently
 *  disagrees." This is visual prominence only; it does not gate report
 *  issuance — that gate already exists via `preliminary_stamp` upstream. */
function TieBar({ tie }: { tie: ConsolidationTie | undefined }) {
  if (!tie) return null

  if (tie.foots) {
    return (
      <div className="mt-2 flex items-start gap-2 text-sm text-emerald-700 bg-emerald-50 border border-emerald-200 rounded px-3 py-2">
        <CheckCircle className="w-4 h-4 mt-0.5 shrink-0" />
        <span>
          Foots — Revenue {fmtMoneyOrDash(tie.subtotal_revenue)} &minus;{' '}
          {fmtMoneyOrDash(tie.eliminated_revenue)} = {fmtMoneyOrDash(tie.expected_revenue)}
          {' · '}
          EBITDA {fmtMoneyOrDash(tie.subtotal_ebitda)} &minus; {fmtMoneyOrDash(tie.eliminated_ebitda)} ={' '}
          {fmtMoneyOrDash(tie.expected_ebitda)}
        </span>
      </div>
    )
  }

  return (
    <div
      role="alert"
      className="mt-2 border-4 border-red-600 rounded px-3 py-2 bg-red-50 text-sm text-red-800"
    >
      <div className="flex items-start gap-2 font-bold">
        <XCircle className="w-4 h-4 mt-0.5 shrink-0" />
        <span>Does not foot</span>
      </div>
      <div className="mt-1 space-y-0.5">
        {!tie.revenue_foots && (
          <p>
            Revenue — expected {fmtMoneyOrDash(tie.expected_revenue)}, engine returned{' '}
            {fmtMoneyOrDash(
              tie.expected_revenue !== null && tie.revenue_diff !== null
                ? tie.expected_revenue + (tie.revenue_diff ?? 0)
                : null,
            )}
            , unexplained difference {fmtMoneyOrDash(tie.revenue_diff)}.
          </p>
        )}
        {!tie.ebitda_foots && (
          <p>
            EBITDA — expected {fmtMoneyOrDash(tie.expected_ebitda)}, engine returned{' '}
            {fmtMoneyOrDash(
              tie.expected_ebitda !== null && tie.ebitda_diff !== null
                ? tie.expected_ebitda + (tie.ebitda_diff ?? 0)
                : null,
            )}
            , unexplained difference {fmtMoneyOrDash(tie.ebitda_diff)}.
          </p>
        )}
        <p className="font-semibold">Sign-off is blocked until this is explained.</p>
      </div>
    </div>
  )
}
